import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { oktaAuth } from './auth';

const isMock = import.meta.env.VITE_OKTA_MOCK === 'true';

export const MOCK_USER = {
  name: 'Jane Smith',
  email: 'jsmith@adobe.com',
  groups: ['GRP-APS-AJO-DESIGN-AGENT-DEV-USERS', 'GRP-APS-AGENTIC-APP-PORTAL-DEV-USERS'],
};

// LOCAL DEV ONLY: when a dev bearer token is injected (auth.ts —
// VITE_DEV_BEARER / localStorage 'ajo_dev_token'), the mock identity must
// match the token's owner, otherwise consultantId-scoped API calls
// (listDesigns, etc.) query the placeholder user and return nothing.
// Decode the token's `sub` claim and use it as the mock email/name.
function deriveMockUser(): typeof MOCK_USER {
  try {
    const tok =
      (typeof localStorage !== 'undefined' && localStorage.getItem('ajo_dev_token')) ||
      import.meta.env.VITE_DEV_BEARER ||
      '';
    if (!tok) return MOCK_USER;
    const payload = JSON.parse(
      atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    return payload?.sub ? { ...MOCK_USER, name: payload.sub, email: payload.sub } : MOCK_USER;
  } catch {
    return MOCK_USER;
  }
}

interface AuthUser {
  name: string;
  email: string;
  groups: string[];
}

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  login: () => {},
  logout: () => {},
  refreshUser: async () => {},
});

// Derive identity from the LOCAL id-token claims — zero network, zero
// awaits. Two prior versions of this function could hang forever and pin
// the app on the boot skeleton: (a) oktaAuth.getUser() hits Okta's
// /userinfo endpoint over the network; (b) `await oktaAuth.isAuthenticated()`
// can block on a pending token renewal, and renewals here go through a
// hidden prompt=none iframe that never resolves when the browser blocks
// third-party cookies. Neither call is needed: the subscribe handler
// already receives authState.isAuthenticated, and the id token in local
// storage already carries name/email/groups.
function loadUserFromIdToken(): AuthUser | null {
  const idToken = oktaAuth.getIdToken();
  if (!idToken) return null;
  try {
    const claims = oktaAuth.token.decode(idToken).payload as Record<string, unknown>;
    // Email must come from a real email claim — `sub` is the Okta user id
    // and would corrupt consultantId scoping if used as a fallback.
    const email = (claims.email as string) || '';
    if (!email) return null;
    return {
      name: (claims.name as string) || email,
      email,
      groups: (claims.groups as string[]) ?? [],
    };
  } catch {
    return null;
  }
}

async function loadUserFromOkta(): Promise<AuthUser | null> {
  const local = loadUserFromIdToken();
  if (local) return local;
  // Fallback (id token missing an email claim — shouldn't happen on this
  // tenant): the old network path, hard-capped so it can never pin
  // isLoading forever.
  const info = await Promise.race([
    oktaAuth.getUser(),
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('userinfo timeout')), 8000),
    ),
  ]);
  return {
    name: info.name ?? (info.email as string) ?? '',
    email: (info.email as string) ?? '',
    groups: [],
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<AuthUser | null>(isMock ? deriveMockUser() : null);
  const [isAuthenticated, setIsAuthenticated] = useState(isMock);
  const [isLoading, setIsLoading] = useState(!isMock);

  useEffect(() => {
    if (isMock) return;

    const applyAuthState = async (authenticated: boolean) => {
      if (!authenticated) {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      try {
        const u = await loadUserFromOkta();
        setUser(u);
        setIsAuthenticated(!!u);
      } catch {
        oktaAuth.tokenManager.clear();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    const unsub = oktaAuth.authStateManager.subscribe((authState) => {
      // eslint-disable-next-line no-console
      console.info('[auth] state change: isAuthenticated =', !!authState.isAuthenticated);
      // The (user, isAuthenticated, isLoading) trio must never expose a
      // gap where isLoading=false but isAuthenticated hasn't been
      // resolved yet — ProtectedRoute reads that gap as "definitely not
      // authed" and fires signInWithRedirect, bouncing a freshly-authed
      // user back through Okta. loadUserFromOkta's happy path is fully
      // synchronous (local id-token decode), so applyAuthState writes
      // all three states in one batched render before its first await.
      // NOTE: deliberately no setIsLoading(true) here — the previous
      // version flipped it on every emission, which reset ProtectedRoute
      // to the skeleton on ROUTINE token refreshes mid-session; combined
      // with a hung network call that was "the page suddenly never
      // loads" on long-lived tabs.
      void applyAuthState(!!authState.isAuthenticated);
    });

    oktaAuth.authStateManager.updateAuthState();

    return () => {
      // Some Okta SDK builds return void from subscribe(); guard so the
      // cleanup doesn't crash the whole app on unmount / token refresh.
      if (typeof unsub === 'function') unsub();
    };
  }, [isMock]);

  const refreshUser = useCallback(async () => {
    if (isMock) return;
    try {
      const u = await loadUserFromOkta();
      setUser(u);
      setIsAuthenticated(!!u);
    } catch {
      // No id token and /userinfo unreachable — leave state as-is.
    }
  }, [isMock]);

  function login() {
    if (isMock) {
      navigate('/');
      return;
    }
    // Don't pass `prompt: 'none'` here — that tells Okta "fail if the user
    // isn't already signed in" which returns a 400 Bad Request when the
    // user lands on this app directly (without first signing into the
    // central portal). Letting the redirect proceed normally works for
    // both flows: portal users hit the existing session and breeze through,
    // direct visitors get the standard Okta login page.
    oktaAuth.signInWithRedirect();
  }

  async function logout() {
    // Sweep all ajo-designs-snapshot:* localStorage entries before sign-out
    try {
      const keys = Object.keys(localStorage);
      for (const key of keys) {
        if (key.startsWith('ajo-designs-snapshot:')) {
          localStorage.removeItem(key);
        }
      }
    } catch {}
    if (isMock) {
      sessionStorage.setItem('ajo:logout-in-progress', '1');
      window.location.replace('/signed-out');
      return;
    }
    // Sentinel: ProtectedRoute reads this flag and SKIPS its
    // signInWithRedirect effect during the brief race window between
    // tokenManager.clear() (which triggers Okta's authStateManager to
    // fire a `isAuthenticated=false` state change → React re-render →
    // ProtectedRoute effect) and the browser actually navigating to
    // /signed-out. Without this, the silent SSO re-auth wins the race
    // and the user lands right back on the protected page — exactly
    // what "sign out doesn't work" looks like. The sentinel is cleared
    // on /signed-out mount.
    sessionStorage.setItem('ajo:logout-in-progress', '1');
    // Fire-and-forget the revoke so we don't block the unload.
    void oktaAuth.revokeAccessToken().catch(() => {});
    oktaAuth.tokenManager.clear();
    window.location.replace('/signed-out');
  }

  return (
    <AuthContext.Provider
      value={{ user, isAuthenticated, isLoading, login, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
